import type { PositionSummary, WalletSummary } from "@papertiger/contracts";

/** Top-line trading metrics derived from one marked portfolio response. */
export interface DashboardMetrics {
  /** Number of positions that still own one or more contracts. */
  currentPositionCount: number;
  /** Average total P&L across current and closed position records, or null without positions. */
  averageProfitPerPositionDollars: number | null;
  /** Count of closed positions with a positive realized P&L. */
  winningPositionCount: number;
  /** Count of closed positions with a negative realized P&L. */
  losingPositionCount: number;
  /** Winning share of closed non-break-even positions on a scale of 100, or null without a result. */
  winRatePercent100: number | null;
}

/**
 * Calculates dashboard metrics without treating unrealized P&L as a win or loss.
 *
 * @param wallet Marked wallet equity summary.
 * @param positions Current and historical aggregate positions.
 * @returns Counts, average P&L, and closed-position win rate.
 */
export function calculateDashboardMetrics(
  wallet: WalletSummary | undefined,
  positions: PositionSummary[],
): DashboardMetrics {
  const currentPositionCount = positions.filter(
    (position) => position.quantity > 0,
  ).length;
  const closedPositions = positions.filter((position) => position.quantity === 0);
  const winningPositionCount = closedPositions.filter(
    (position) => position.realizedProfitDollars > 0,
  ).length;
  const losingPositionCount = closedPositions.filter(
    (position) => position.realizedProfitDollars < 0,
  ).length;
  const decidedPositionCount = winningPositionCount + losingPositionCount;

  return {
    currentPositionCount,
    averageProfitPerPositionDollars:
      wallet === undefined || positions.length === 0
        ? null
        : wallet.totalProfitDollars / positions.length,
    winningPositionCount,
    losingPositionCount,
    winRatePercent100:
      decidedPositionCount === 0
        ? null
        : (winningPositionCount / decidedPositionCount) * 100,
  };
}
